import { useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * 视频浮窗尺寸与锚定定位逻辑抽离。
 * 输出：
 * - videoWindowStyle: 计算出的 width/height（不含定位）
 * - videoDockStyle: 完整 position/left/top/bottom/transform 等定位（用于 VideoWindow）
 */
export function useVideoDocking({
  currentMusic,
  isFullscreen,
  shouldShowVideoWindow,
  isVideoFullscreen,
  playerRef,
  getVideoFilePath,
  videoDimensions,
  coverDimensions,
  showCover
}) {
  const [videoWindowStyle, setVideoWindowStyle] = useState({});
  const [videoDockStyle, setVideoDockStyle] = useState(null);
  const [dockingReady, setDockingReady] = useState(false);
  const viewportKeyRef = useRef(''); // `${w}x${h}`

  // 监听视频尺寸变化：这里只负责"算宽高"，不负责"定位"
  useEffect(() => {
    const hasVideo = currentMusic ? !!getVideoFilePath(currentMusic) : false;
    const hasVisualWindow = hasVideo || showCover;
    if (!hasVisualWindow || isFullscreen || !shouldShowVideoWindow) {
      if (!hasVisualWindow || !shouldShowVideoWindow) setVideoWindowStyle({});
      setDockingReady(false);
      return;
    }

    const updateVideoWindowSize = (force = false) => {
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      const key = `${windowWidth}x${windowHeight}`;
      const viewportChanged = key !== viewportKeyRef.current;
      if (viewportChanged) viewportKeyRef.current = key;
      if (!force && !viewportChanged) return;

      const hasMeta = !!(videoDimensions.width && videoDimensions.height);
      const hasCoverMeta = !!(coverDimensions?.width && coverDimensions?.height);
      const videoAspectRatio = hasVideo
        ? (hasMeta ? (videoDimensions.width / videoDimensions.height) : (16 / 9))
        : (hasCoverMeta ? (coverDimensions.width / coverDimensions.height) : 1);
      const isMobile = windowWidth < 768;
      
      let finalWidth;
      let finalHeight;
      
      if (isMobile) {
        // 手机竖屏：宽度 = 屏幕宽度，高度按横纵比计算
        finalWidth = windowWidth;
        finalHeight = Math.floor(finalWidth / videoAspectRatio);
      } else {
        // 桌面端：保持原有逻辑
        const leftPad = 8;
        const rightPad = 8;
        const maxWidth = Math.max(0, windowWidth - leftPad - rightPad);
        const maxHeight = windowHeight * 0.4;
        const preferredWidth = hasMeta ? videoDimensions.width : Math.min(640, windowWidth);
        let targetWidth = Math.min(maxWidth || windowWidth, preferredWidth);
        let targetHeight = targetWidth / videoAspectRatio;
        if (targetHeight > maxHeight) {
          targetHeight = maxHeight;
          targetWidth = targetHeight * videoAspectRatio;
        }
        finalWidth = Math.min(maxWidth || windowWidth, Math.max(240, Math.floor(targetWidth)));
        finalHeight = Math.max(135, Math.floor(finalWidth / videoAspectRatio));
      }

      setVideoWindowStyle({
        width: `${finalWidth}px`,
        height: `${finalHeight}px`
      });
    };

    updateVideoWindowSize(true);
    const onResize = () => updateVideoWindowSize(true);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [currentMusic, isFullscreen, videoDimensions, coverDimensions, shouldShowVideoWindow, getVideoFilePath, showCover]);

  // 关键：让视频窗口“锚定播放器”——用真实播放器 rect 来定位
  useLayoutEffect(() => {
    const hasVideo = currentMusic ? !!getVideoFilePath(currentMusic) : false;
    const hasVisualWindow = hasVideo || showCover;
    const playerEl = playerRef.current;

    if (!hasVisualWindow || isFullscreen || !shouldShowVideoWindow) {
      setVideoDockStyle(null);
      setDockingReady(false);
      return;
    }

    // 视频窗口底边与播放器顶边间距（px）
    // 需求：始终留缝
    const gap = 4;
    const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
    const readPx = (v) => {
      if (!v) return null;
      const n = typeof v === 'number' ? v : parseFloat(String(v));
      return Number.isFinite(n) ? n : null;
    };

    const getViewportKey = () => {
      try {
        const w = window.innerWidth || 0;
        const h = window.innerHeight || 0;
        return `${w}x${h}`;
      } catch (_) {
        return '';
      }
    };

    const currentKey = getViewportKey();
    if (currentKey && !viewportKeyRef.current) viewportKeyRef.current = currentKey;

    if (!playerEl) {
      const w = readPx(videoWindowStyle?.width) || 640;
      const h = readPx(videoWindowStyle?.height) || 360;
      setVideoDockStyle({
        position: 'fixed',
        width: `${w}px`,
        height: `${h}px`,
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 40,
        boxSizing: 'border-box'
      });
      // playerRef 未就绪时等待下次 effect 进入（playerEl 出现）再定位
      setDockingReady(false);
      return;
    }

    const compute = () => {
      try {
        const rect = playerEl.getBoundingClientRect();
        const isMobile = window.innerWidth < 768;
        const navHeight = (() => {
          try {
            const headerEl = document.querySelector('.site-header');
            const h = headerEl?.getBoundingClientRect?.().height;
            return Number.isFinite(h) && h > 0 ? h : 0;
          } catch (_) {
            return 0;
          }
        })();
        const w = readPx(videoWindowStyle?.width) || 640;
        const h = readPx(videoWindowStyle?.height) || 360;
        const hasMeta = !!(videoDimensions.width && videoDimensions.height);
        const hasCoverMeta = !!(coverDimensions?.width && coverDimensions?.height);
        const videoAspectRatio = hasVideo
          ? (hasMeta ? (videoDimensions.width / videoDimensions.height) : (16 / 9))
          : (hasCoverMeta ? (coverDimensions.width / coverDimensions.height) : 1);
        const shouldForceFullWidth = !!isVideoFullscreen;
        const frameExtra = shouldForceFullWidth ? 16 : 0; // border(4px*2) + padding(4px*2)
        const maxOuterWidth = Math.max(0, window.innerWidth);
        const maxOuterHeight = Math.max(0, Math.min(window.innerHeight, Math.floor(rect.top - gap - navHeight)));

        let finalWidth;
        let finalHeight;
        if (shouldForceFullWidth) {
          const maxContentWidth = Math.max(0, maxOuterWidth - frameExtra);
          let contentWidth = maxContentWidth;
          let contentHeight = contentWidth / videoAspectRatio;
          let outerWidth = contentWidth + frameExtra;
          let outerHeight = contentHeight + frameExtra;
          if (maxOuterHeight > 0 && outerHeight > maxOuterHeight) {
            outerHeight = maxOuterHeight;
            contentHeight = Math.max(0, outerHeight - frameExtra);
            contentWidth = contentHeight * videoAspectRatio;
            outerWidth = contentWidth + frameExtra;
          }
          finalWidth = Math.max(0, Math.floor(outerWidth));
          finalHeight = Math.max(0, Math.floor(outerHeight));
        } else if (isMobile) {
          // 手机竖屏：使用屏幕宽度和计算的高度
          finalWidth = window.innerWidth;
          finalHeight = h;
        } else {
          // 桌面端：保持原有逻辑
          const leftPad = 8;
          const rightPad = 8;
          const maxW = Math.max(0, window.innerWidth - leftPad - rightPad);
          finalWidth = Math.min(maxW || window.innerWidth, w);
          finalHeight = h;
        }

        const left = shouldForceFullWidth
          ? Math.max(0, Math.floor((window.innerWidth - finalWidth) / 2))
          : (isMobile
              ? 0  // 手机竖屏：左对齐（宽度已经是屏幕宽度）
              : (() => {
                  const leftPad = 8;
                  const rightPad = 8;
                  const maxW = Math.max(0, window.innerWidth - leftPad - rightPad);
                  return clamp(rect.left + rect.width / 2 - finalWidth / 2, leftPad, window.innerWidth - finalWidth - rightPad);
                })());
        const rawTop = Math.floor(rect.top - gap - finalHeight);
        const top = Math.max(Math.floor(navHeight), rawTop);
        setVideoDockStyle({
          position: 'fixed',
          width: `${finalWidth}px`,
          height: `${finalHeight}px`,
          left: `${left}px`,
          top: `${top}px`,
          bottom: 'auto',
          zIndex: 40,
          boxSizing: 'border-box'
        });
        setDockingReady(true);
      } catch (_) {}
    };

    // 只在“播放器滑入完成/窗口尺寸变化”时重算一次，然后锁定。
    // 这样不会因为播放器内部状态（按钮显示/隐藏、字幕区等）变化而抖动。
    const lockOnce = () => {
      compute();
      viewportKeyRef.current = getViewportKey() || viewportKeyRef.current;
    };

    const parseTimeListToMs = (raw) => {
      const list = String(raw || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      if (!list.length) return 0;
      const toMs = (v) => {
        const s = String(v).trim();
        if (!s) return 0;
        if (s.endsWith('ms')) {
          const n = parseFloat(s.slice(0, -2));
          return Number.isFinite(n) ? n : 0;
        }
        if (s.endsWith('s')) {
          const n = parseFloat(s.slice(0, -1));
          return Number.isFinite(n) ? n * 1000 : 0;
        }
        const n = parseFloat(s);
        return Number.isFinite(n) ? n * 1000 : 0;
      };
      return Math.max(...list.map(toMs));
    };

    const getTransitionMs = () => {
      try {
        const cs = window.getComputedStyle(playerEl);
        const d = parseTimeListToMs(cs.transitionDuration);
        const delay = parseTimeListToMs(cs.transitionDelay);
        return Math.max(0, d + delay);
      } catch (_) {
        return 0;
      }
    };

    // 进入播放时播放器会从底部滑入：等待 transform transition 结束后再锁定定位。
    setDockingReady(false);
    const transitionMs = getTransitionMs();
    const onTransitionEnd = (e) => {
      try {
        if (!e || e.target !== playerEl) return;
        if (e.propertyName && e.propertyName !== 'transform') return;
        lockOnce();
      } catch (_) {}
    };
    playerEl.addEventListener('transitionend', onTransitionEnd);

    // 兜底：如果 transitionend 没触发（例如 reduced motion / 某些浏览器情况），按时长+1帧锁定
    const timeoutMs = Math.min(1200, Math.max(0, transitionMs) + 34);
    const timeoutId = window.setTimeout(() => lockOnce(), timeoutMs);

    // 额外兜底：如果根本没有过渡，下一帧就锁定
    let raf2Id = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2Id = requestAnimationFrame(() => {
        if (getTransitionMs() <= 0) lockOnce();
      });
    });

    const onResize = () => {
      lockOnce();
    };
    window.addEventListener('resize', onResize);

    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        compute();
      });
      try { resizeObserver.observe(playerEl); } catch (_) {}
    }

    return () => {
      window.removeEventListener('resize', onResize);
      try { playerEl.removeEventListener('transitionend', onTransitionEnd); } catch (_) {}
      try { resizeObserver?.disconnect?.(); } catch (_) {}
      try { window.clearTimeout(timeoutId); } catch (_) {}
      try { cancelAnimationFrame(raf1); } catch (_) {}
      try { if (raf2Id) cancelAnimationFrame(raf2Id); } catch (_) {}
    };
  }, [
    currentMusic,
    isFullscreen,
    shouldShowVideoWindow,
    isVideoFullscreen,
    playerRef,
    getVideoFilePath,
    videoWindowStyle,
    videoDimensions?.width,
    videoDimensions?.height,
    coverDimensions?.width,
    coverDimensions?.height,
    showCover
  ]);

  return { videoWindowStyle, videoDockStyle, dockingReady };
}

